import { Button, Input } from '@cloudflare/kumo';
import { ArrowRight, EnvelopeSimple } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { oauthRedirectSchema } from '../../shared/oauth.ts';
import { api, queryClient, unwrap } from '../api.ts';
import googleG from '../assets/google-g.svg';
import wirechunkLogo from '../assets/wirechunk-logo.svg';
import { ErrorNotice } from '../common.tsx';
import { safeReturnTo } from '../return-to.ts';

type AuthMode = 'sign-in' | 'sign-up' | 'recovery' | 'reset';
type FieldErrors = { name?: string; email?: string; password?: string };

function fieldErrors(mode: AuthMode, values: { name: string; email: string; password: string }): FieldErrors {
  const errors: FieldErrors = {};
  if (mode === 'sign-up' && !values.name.trim()) errors.name = 'Enter your name.';
  if (mode !== 'reset') {
    const email = values.email.trim();
    const at = email.indexOf('@');
    const domain = email.slice(at + 1);
    const dot = domain.indexOf('.');
    if (!email) errors.email = 'Enter your email.';
    else if (at < 1 || dot < 1 || dot === domain.length - 1) errors.email = 'Enter a valid email address.';
  }
  if (mode !== 'recovery') {
    if (!values.password) errors.password = mode === 'reset' ? 'Enter a new password.' : 'Enter a password.';
    else if ((mode === 'reset' || mode === 'sign-up') && values.password.length < 10)
      errors.password = 'Use at least 10 characters.';
  }
  return errors;
}

export function AuthenticationPage({ mode }: { mode: AuthMode }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sent, setSent] = useState(false);
  const search = useSearch({ strict: false });
  const oauthQuery =
    search.oauthQuery ??
    (new URLSearchParams(location.search).has('sig') ? location.search.slice(1) : undefined);
  const returnTo = oauthQuery ? `/authorize?${oauthQuery}` : safeReturnTo(search.returnTo);
  const authSearch = { returnTo, ...(oauthQuery ? { oauthQuery } : {}) };
  const submit = useMutation({
    mutationFn: async () => {
      if (mode === 'sign-up')
        return unwrap(
          await api.POST('/api/auth/sign-up/email', {
            body: {
              name: name.trim(),
              email: email.trim(),
              password,
              ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
            },
          }),
        );
      if (mode === 'recovery')
        return unwrap(
          await api.POST('/api/auth/request-password-reset', {
            body: {
              email: email.trim(),
              redirectTo: `${location.origin}/reset-password?returnTo=${encodeURIComponent(returnTo)}`,
            },
          }),
        );
      if (mode === 'reset')
        return unwrap(
          await api.POST('/api/auth/reset-password', {
            body: { newPassword: password, token: search.token },
          }),
        );
      return unwrap(
        await api.POST('/api/auth/sign-in/email', {
          body: { email: email.trim(), password, ...(oauthQuery ? { oauth_query: oauthQuery } : {}) },
        }),
      );
    },
    onSuccess: async (result) => {
      if (mode === 'sign-in' || mode === 'sign-up') {
        await queryClient.invalidateQueries();
        const redirect = oauthRedirectSchema.safeParse(result);
        location.assign(redirect.success && redirect.data.redirect ? redirect.data.url : returnTo);
      } else setSent(true);
    },
  });
  const google = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/auth/sign-in/social', {
          body: {
            provider: 'google',
            callbackURL: `${location.origin}${returnTo}`,
            ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
          },
        }),
      ),
    onSuccess: (result) => {
      if (result.url) location.assign(result.url);
    },
  });
  const title =
    mode === 'sign-up'
      ? 'Sign up'
      : mode === 'recovery'
        ? 'Reset your password'
        : mode === 'reset'
          ? 'Choose a new password'
          : 'Sign in to Wirechunk';
  return (
    <main className="p-6 min-h-screen bg-kumo-tint min-[761px]:px-12 min-[761px]:py-8.5">
      <div className="mx-auto max-w-265 mt-10 mb-0 block grid-cols-[1.15fr_1fr] items-center gap-10 min-[761px]:mx-auto min-[761px]:grid min-[761px]:mt-[9vh] min-[761px]:mb-0 min-[1051px]:gap-22.5">
        <aside className="flex justify-center text-kumo-default pb-5 min-[761px]:block min-[761px]:pb-12">
          <div className="inline-flex items-center min-[761px]:mb-7 [&_img]:block [&_img]:h-7.5">
            <img src={wirechunkLogo} width="160" height="32" alt="Wirechunk" />
          </div>
          <h1 className="hidden m-0 text-[30px] leading-[1.35] font-semibold tracking-[-0.8px] min-[761px]:block min-[1051px]:text-[clamp(32px,3.5vw,46px)]">
            Your business.
            <br />
            Easy to understand.
            <br />
            Easy to reach.
          </h1>
          <p className="hidden mx-0 mt-5.5 mb-0 max-w-87.5 text-[16px] leading-[1.65] text-kumo-subtle min-[761px]:block">
            Build websites optimized for AI agents.
          </p>
        </aside>
        <section className="m-auto p-7 border border-kumo-line bg-kumo-base rounded-[16px] max-w-110 shadow-[0_10px_36px_var(--color-kumo-shadow-drop)] [&_>_p]:mx-0 [&_>_p]:mt-[9px] [&_>_p]:mb-6.5 [&_h2]:m-0 [&_h2]:text-[24px] [&_h2]:font-semibold [&_h2]:tracking-[-0.35px] [&_form_button[type=submit]]:mt-3 [&_form_button[type=submit]]:w-full min-[761px]:m-0 min-[761px]:p-9 min-[761px]:max-w-none">
          {sent ? (
            <div className="[&_a]:text-kumo-brand [&_a]:underline [&_a]:underline-offset-[3px] grid gap-4.5 [&_p]:m-0 [&_p]:leading-[1.65] [&_p]:text-kumo-subtle">
              <EnvelopeSimple size={36} />
              <h2 className="text-[20px] font-semibold tracking-[-0.35px]">
                {mode === 'reset' ? 'Password updated' : 'Check your email'}
              </h2>
              <p className="leading-[1.65]">
                {mode === 'reset'
                  ? 'You can now sign in with your new password.'
                  : mode === 'recovery'
                    ? 'If this email has an account, we’ve sent a password reset link.'
                    : 'We’ve sent a link to verify your email and finish setup.'}
              </p>
              <Link to="/sign-in" search={authSearch}>
                Back to sign in
              </Link>
              {import.meta.env.DEV && (
                <a href="/__dev/mail" target="_blank" rel="noreferrer">
                  Open development mailbox
                </a>
              )}
            </div>
          ) : (
            <>
              <h2 className="text-[20px] font-semibold tracking-[-0.35px]">{title}</h2>
              <form
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  const next = fieldErrors(mode, { name, email, password });
                  setErrors(next);
                  if (next.name || next.email || next.password) return;
                  submit.mutate();
                }}
                className="stack grid gap-5 content-start mt-6.5"
              >
                {mode === 'sign-up' && (
                  <Input
                    label="Name"
                    value={name}
                    onChange={(event) => {
                      setName(event.target.value);
                      setErrors((current) => ({ ...current, name: undefined }));
                    }}
                    autoComplete="name"
                    required
                    error={errors.name}
                  />
                )}
                {mode !== 'reset' && (
                  <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setErrors((current) => ({ ...current, email: undefined }));
                    }}
                    autoComplete="email"
                    required
                    error={errors.email}
                  />
                )}
                {mode !== 'recovery' && (
                  <Input
                    label={mode === 'reset' ? 'New password' : 'Password'}
                    type="password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setErrors((current) => ({ ...current, password: undefined }));
                    }}
                    autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
                    required
                    error={errors.password}
                  />
                )}
                <ErrorNotice error={submit.error ?? google.error} />
                <Button type="submit" variant="primary" size="lg" disabled={submit.isPending}>
                  {submit.isPending
                    ? 'Working…'
                    : mode === 'sign-up'
                      ? 'Sign up'
                      : mode === 'recovery'
                        ? 'Send reset link'
                        : mode === 'reset'
                          ? 'Update password'
                          : 'Sign in'}
                  <ArrowRight size={18} />
                </Button>
              </form>
              {(mode === 'sign-in' || mode === 'sign-up') && (
                <Button
                  className="mt-6 w-full [&_img]:w-5 [&_img]:h-5 [&_img]:mx-1.5"
                  onClick={() => google.mutate()}
                  disabled={google.isPending}
                >
                  <img src={googleG} width={20} height={20} alt="" />
                  Sign in with Google
                </Button>
              )}
              <div className="mt-6 text-[13px] text-kumo-subtle text-center grid gap-4 [&_a]:text-kumo-brand [&_a]:underline [&_a]:underline-offset-[3px]">
                {mode === 'sign-in' ? (
                  <>
                    <Link to="/forgot-password" search={authSearch}>
                      Forgot password?
                    </Link>
                    <p className="leading-[1.65]">
                      New to Wirechunk?{' '}
                      <Link to="/sign-up" search={authSearch}>
                        Sign up
                      </Link>
                    </p>
                  </>
                ) : (
                  <p className="leading-[1.65]">
                    Already have an account?{' '}
                    <Link to="/sign-in" search={authSearch}>
                      Sign in
                    </Link>
                  </p>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
